import { getAdminApi, request } from "../client"
import { parseGuideCourse, parseGuideLesson } from "./_logic/validation"

export type {
  GuideCourse,
  GuideLesson,
  GuideLessonSummary,
  GuideImage,
  GuideImageMarker,
  GuideBasicBlock,
  GuideBlock,
  GuideChoice,
  GuideStage,
  GuideEntryPoint,
  GuideEntryTarget,
} from "./types"

export const KEHRBLATT_GUIDE_COURSE_KEY = "kehrblatt-guide" as const
const coursePath = `/tutorials/${KEHRBLATT_GUIDE_COURSE_KEY}`

/** Return the public row identifier of this login, never its authentication token. */
export const fetchLearningLoginId = async (): Promise<string | null> => {
  const response = await getAdminApi().auth.sessions()

  return response.items.find((session) => session.current)?.session_id ?? null
}

/** Read only summary metadata and the authenticated user's completion state. */
export const fetchGuideCourse = async (options: { signal?: AbortSignal } = {}) =>
  parseGuideCourse(await request<unknown>(coursePath, { signal: options.signal }))

/** Fetch one chapter; images are requested by the browser only when displayed. */
export const fetchGuideLesson = async (
  lessonKey: string,
  options: { signal?: AbortSignal } = {},
) => {
  const lesson = parseGuideLesson(
    await request<unknown>(`${coursePath}/lessons/${encodeURIComponent(lessonKey)}`, {
      signal: options.signal,
    }),
  )

  if (lesson.lesson.key !== lessonKey) throw new Error("learning_guide_lesson_mismatch")

  return lesson
}

/** Idempotently set a version-bound completion, never a toggle or a video completion. */
export const setGuideLessonCompletion = async (
  lessonKey: string,
  payload: { completed: boolean; content_version: number },
  options: { signal?: AbortSignal } = {},
) =>
  parseGuideCourse(
    await request<unknown>(`${coursePath}/lessons/${encodeURIComponent(lessonKey)}/completion`, {
      method: "PUT",
      body: JSON.stringify(payload),
      signal: options.signal,
    }),
  )
